import { useCallback, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";

/**
 * Reusable cascading geographic drop-downs used by Celebrate a Life,
 * Events composer, Family Trees, etc.
 *
 * Renders: Country → State → County/District → City (free-form) → Zip.
 * Loads options from the existing /api/locations catalogue.
 *
 * value shape: { country, state, county, city, zip }
 * onChange(nextValue) is fired on every field change.
 */
export default function LocationCascade({ value = {}, onChange, testidPrefix = "loc" }) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [subdivs, setSubdivs] = useState([]); // county OR district
  const [loading, setLoading] = useState(false);

  const v = value || {};
  const border = { borderColor: "var(--wofy-border)" };
  const selectCls = "w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent";

  // Load countries once
  useEffect(() => {
    let alive = true;
    setLoading(true);
    http
      .get("/locations?kind=country&limit=200")
      .then((r) => {
        if (alive) setCountries((r.data?.items || []).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Load states when country changes
  const countryKey = v.country || "";
  useEffect(() => {
    if (!countryKey) {
      setStates([]);
      return;
    }
    let alive = true;
    http
      .get(`/locations?kind=state&country=${encodeURIComponent(countryKey)}&limit=200`)
      .then((r) => {
        if (alive) setStates((r.data?.items || []).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => alive && setStates([]));
    return () => {
      alive = false;
    };
  }, [countryKey]);

  // Load county OR district when state changes.
  // Convention: India → district, everywhere else → county.
  const stateKey = v.state || "";
  const subdivKind = useMemo(() => {
    if (!countryKey) return "county";
    return /india/i.test(countryKey) ? "district" : "county";
  }, [countryKey]);

  useEffect(() => {
    if (!countryKey || !stateKey) {
      setSubdivs([]);
      return;
    }
    let alive = true;
    http
      .get(
        `/locations?kind=${subdivKind}&country=${encodeURIComponent(countryKey)}&state=${encodeURIComponent(stateKey)}&limit=200`
      )
      .then((r) => {
        if (alive) setSubdivs((r.data?.items || []).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => alive && setSubdivs([]));
    return () => {
      alive = false;
    };
  }, [countryKey, stateKey, subdivKind]);

  const update = useCallback(
    (patch) => {
      onChange && onChange({ ...v, ...patch });
    },
    [v, onChange]
  );

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-wider">Country</span>
        <select
          value={v.country || ""}
          onChange={(e) => update({ country: e.target.value, state: "", county: "", city: "" })}
          className={selectCls}
          style={border}
          data-testid={`${testidPrefix}-country`}
        >
          <option value="">— Select country —</option>
          {countries.map((c) => (
            <option key={c.id || c.slug || c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-wider">State / Province</span>
        <select
          value={v.state || ""}
          onChange={(e) => update({ state: e.target.value, county: "", city: "" })}
          className={selectCls}
          style={border}
          disabled={!v.country || states.length === 0}
          data-testid={`${testidPrefix}-state`}
        >
          <option value="">
            {v.country ? (states.length ? "— Select state —" : "— No states available —") : "— Select country first —"}
          </option>
          {states.map((s) => (
            <option key={s.id || s.slug || s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-wider">
          {subdivKind === "district" ? "District" : "County"} <span className="opacity-60">(optional)</span>
        </span>
        {subdivs.length > 0 ? (
          <select
            value={v.county || ""}
            onChange={(e) => update({ county: e.target.value })}
            className={selectCls}
            style={border}
            data-testid={`${testidPrefix}-county`}
          >
            <option value="">— Select {subdivKind} —</option>
            {subdivs.map((s) => (
              <option key={s.id || s.slug || s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={v.county || ""}
            onChange={(e) => update({ county: e.target.value })}
            placeholder={`Enter ${subdivKind}`}
            className={selectCls}
            style={border}
            data-testid={`${testidPrefix}-county-free`}
          />
        )}
      </label>

      <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-wider">City / Village</span>
        <input
          value={v.city || ""}
          onChange={(e) => update({ city: e.target.value })}
          placeholder="City or village"
          className={selectCls}
          style={border}
          data-testid={`${testidPrefix}-city`}
        />
      </label>

      <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-wider">
          Zip / Postal code <span className="opacity-60">(optional)</span>
        </span>
        <input
          value={v.zip || ""}
          onChange={(e) => update({ zip: e.target.value })}
          placeholder="e.g. 682001"
          className={selectCls}
          style={border}
          data-testid={`${testidPrefix}-zip`}
        />
      </label>

      {loading && (
        <div className="md:col-span-2 text-[11px] font-mono uppercase tracking-wider opacity-60">
          Loading location catalogue…
        </div>
      )}
    </div>
  );
}
